import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, ListChecks, FileText, LogOut, CheckSquare, ShieldCheck, ChevronRight, Contact, Bell, Menu, Shield, Send, Server, History, UserCog, Inbox } from "lucide-react";
import { useAuth } from "@/lib/auth";
import MobileNav from "@/components/MobileNav";
import BackendVersionBadge from "@/components/BackendVersionBadge";
import DeploySyncBanner from "@/components/DeploySyncBanner";

const NAV = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/admin/partners", icon: Users, label: "Partners" },
    { to: "/admin/transactions", icon: ListChecks, label: "Transactions" },
    { to: "/admin/approvals", icon: CheckSquare, label: "Approvals" },
    { to: "/admin/customers", icon: Contact, label: "Customers" },
    { to: "/admin/invoices", icon: FileText, label: "Invoices" },
    { to: "/admin/alerts", icon: Bell, label: "Alerts" },
    { to: "/admin/security", icon: Shield, label: "Security" },
    { to: "/admin/payouts", icon: Send, label: "Payouts" },
    { to: "/admin/payin", icon: Inbox, label: "Payin" },
    { to: "/admin/vendors", icon: Server, label: "Vendors & MIDs", superOnly: true },
    { to: "/admin/users", icon: UserCog, label: "Admin Users", superOnly: true },
    { to: "/admin/audit", icon: History, label: "Audit Log", superOnly: true },
];

export default function AdminShell() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const onLogout = async () => { await logout(); nav("/admin/login"); };
    const crumb = NAV.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)))?.label || "Dashboard";
    // Only super_admins see the Vendors & MIDs link; regular admins get the rest.
    const isSuper = user?.role === "super_admin";
    const visibleNav = NAV.filter((it) => !it.superOnly || isSuper);

    return (
        <div className="flex min-h-screen bg-[#FAFBFC]">
            <aside className="hidden lg:flex flex-col w-60 border-r border-slate-200 bg-white sticky top-0 h-screen">
                <div className="px-5 py-5 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">P</div>
                        <div>
                            <div className="font-bold text-sm leading-tight">PayGateway</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5 flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5" /> Admin</div>
                        </div>
                    </div>
                </div>
                <nav className="flex-1 p-3 space-y-0.5">
                    {visibleNav.map((it) => (
                        <NavLink data-testid={`nav-admin-${it.label.toLowerCase()}`} key={it.to} to={it.to} end={it.end}
                            className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                                isActive ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600 hover:bg-slate-50"
                            }`}>
                            <it.icon className="w-4 h-4" />
                            {it.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-slate-100">
                    <div className="px-3 py-2 mb-1">
                        <div className="text-xs text-slate-400">Signed in</div>
                        <div className="text-sm font-medium truncate">{user?.email}</div>
                    </div>
                    <button data-testid="admin-logout" onClick={onLogout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50">
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0">
                <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                    <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            {/* Hamburger — only renders below `lg`. */}
                            <button
                                data-testid="admin-mobile-menu"
                                onClick={() => setMobileOpen(true)}
                                aria-label="Open navigation"
                                className="lg:hidden p-2 -ml-1.5 rounded-md text-slate-600 hover:bg-slate-100"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                            <div className="text-sm text-slate-500 flex items-center gap-1.5 min-w-0">
                                <span className="hidden sm:inline">Admin</span>
                                <ChevronRight className="w-3.5 h-3.5 hidden sm:inline" />
                                <span className="font-medium text-slate-900 truncate">{crumb}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <BackendVersionBadge />
                            <span className={`pill ${isSuper ? "pill-info" : "pill-muted"}`} data-testid="admin-role-pill">
                                {isSuper ? "Super Admin" : "Admin"}
                            </span>
                        </div>
                    </div>
                </header>
                <DeploySyncBanner />
                <div className="p-4 sm:p-6 fade-up">
                    <Outlet />
                </div>
            </main>
            <MobileNav
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                navItems={visibleNav}
                variant="admin"
                user={user}
                onLogout={onLogout}
                testIdPrefix="admin"
            />
        </div>
    );
}
